import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './GroundwaterDashboard.css';
import { FaTint, FaMapMarkerAlt, FaChartBar, FaChartLine, FaFire, FaCircle } from 'react-icons/fa';
import TrendAnalysis from '../components/TrendAnalysis';
import VillageDataPanel from '../components/VillageDataPanel';
import VillageMapLayer from '../components/VillageMapLayer';
import DistrictVillageLabel from '../components/DistrictVillageLabel';
import StateVillageLabel from '../components/StateVillageLabel';
import VillageDetailModal from '../components/VillageDetailModal';
import YearSelector from '../components/YearSelector';
import DistrictTrendsPanel from '../components/DistrictTrendsPanel';
import GroundwaterSearchFilter from '../components/GroundwaterSearchFilter';


// Component to handle map zoom and center
const MapController = ({ center, zoom, onZoomChange }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && zoom) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  // Invalidate map size on mount so it fills its container correctly on mobile
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    return () => clearTimeout(timer);
  }, [map]);
  
  // Track zoom changes
  useEffect(() => {
    const handleZoomEnd = () => {
      if (onZoomChange) {
        onZoomChange(map.getZoom());
      }
    };
    
    map.on('zoomend', handleZoomEnd);
    
    // Initial zoom
    if (onZoomChange) {
      onZoomChange(map.getZoom());
    }
    
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onZoomChange]);
  
  return null;
};

const GroundwaterDashboard = () => {
  const [geoData, setGeoData] = useState(null);
  const [stateGeoData, setStateGeoData] = useState(null); // State-level GeoJSON
  const [districtGeoData, setDistrictGeoData] = useState(null); // District-level GeoJSON
  const [selectedLocation, setSelectedLocation] = useState('INDIA');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [mapCenter, setMapCenter] = useState([23.5, 82]);
  const [mapZoom, setMapZoom] = useState(4.6);
  const [loading, setLoading] = useState(true);
  
  // User location from login
  const [userState, setUserState] = useState(null);
  const [userDistrict, setUserDistrict] = useState(null);
  const [highlightUserLocation, setHighlightUserLocation] = useState(false);
  const [statesData, setStatesData] = useState([]);
  const [currentStateData, setCurrentStateData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [allDistrictsData, setAllDistrictsData] = useState({}); // Cache all district data by state
  const [districtMapping, setDistrictMapping] = useState(null); // District name mapping
  const [dataLoaded, setDataLoaded] = useState(false); // Track if all data is loaded
  const [selectedYear, setSelectedYear] = useState('2024-2025'); // Year selection state
  const [activeTab, setActiveTab] = useState('map'); // Tab selection: 'map' or 'trends'
  
  // Comparison mode states
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonYear1, setComparisonYear1] = useState('2019-2020');
  const [comparisonYear2, setComparisonYear2] = useState('2024-2025');
  const [comparisonData, setComparisonData] = useState(null);
  const [featureColorMap, setFeatureColorMap] = useState({}); // Pre-calculated colors for each feature
  
  // Village-level data states
  const [villageData, setVillageData] = useState([]);
  const [filteredVillageData, setFilteredVillageData] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [villageMapMode, setVillageMapMode] = useState('markers'); // 'markers', 'heatmap', 'off'
  const [hoveredVillage, setHoveredVillage] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(5); // Track current zoom level
  const [showVillageModal, setShowVillageModal] = useState(false); // Modal visibility
  const [villageDataYear, setVillageDataYear] = useState('2024'); // Year for village data display
  
  // Ref for village detail section
  const villageDetailRef = useRef(null);
  
  // Cached aggregations for progressive disclosure
  const [villageDataByState, setVillageDataByState] = useState({}); // State-level aggregation
  const [villageDataByDistrict, setVillageDataByDistrict] = useState({}); // District-level aggregation
  const [villageDataCache, setVillageDataCache] = useState(null); // Cache raw village data
  
  // Load user location from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const role = user.role || 'farmer';
        
        // Store user info
        if (user.selectedState) {
          setUserState(user.selectedState);
        }
        if (user.selectedDistrict) {
          setUserDistrict(user.selectedDistrict);
        }
        
        // Auto-select and zoom for state/district admins ONLY
        if (role === 'state' && user.selectedState) {
          setHighlightUserLocation(true);
          setSelectedState(user.selectedState);
          setSelectedLocation(user.selectedState);
          
          // Zoom to user's state
          setTimeout(() => {
            zoomToState(user.selectedState);
          }, 1000);
          
          console.log('State admin - auto-selected:', user.selectedState);
        } else if (role === 'district' && user.selectedState && user.selectedDistrict) {
          setHighlightUserLocation(true);
          setSelectedState(user.selectedState);
          setSelectedDistrict(user.selectedDistrict);
          setSelectedLocation(`${user.selectedDistrict}, ${user.selectedState}`);
          
          // Zoom to user's district
          setTimeout(() => {
            zoomToState(user.selectedState);
          }, 1000);
          
          console.log('District admin - auto-selected:', user.selectedState, user.selectedDistrict);
        } else {
          // Farmers can explore freely - no auto-selection
          console.log('Farmer - free exploration mode');
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Available years configuration
  const availableYears = [
    { value: '2024-2025', label: '2024-2025', file: 'groundwater-data.json' },
    { value: '2023-2024', label: '2023-2024', file: 'groundwater-data-2023-2024.json' },
    { value: '2022-2023', label: '2022-2023', file: 'groundwater-data-2022-2023.json' },
    { value: '2021-2022', label: '2021-2022', file: 'groundwater-data-2021-2022.json' },
    { value: '2019-2020', label: '2019-2020', file: 'groundwater-data-2019-2020.json' }
  ];

  // Category colors matching specification
  const categoryColors = {
    'Safe': '#1fc21f',
    'Semi Critical': '#f9c802',
    'Critical': '#ff8d1a',
    'Over Exploited': '#ea2328',
    'Saline': '#2ba6ff',
    // 'Hilly Area': '#969595',
    'No Data': '#e6e6e6'
  };

  // Comparison colors for change classification
  const comparisonColors = {
    'significantly_improved': '#0d7a0d',
    'improved': '#4caf50',
    'neutral': '#9e9e9e',
    'degraded': '#ff9800',
    'significantly_degraded': '#d32f2f',
    'no_data': '#e6e6e6',
    'No Data': '#e6e6e6'
  };

  // Helper function to normalize state names for matching
  const normalizeStateName = (name) => {
    if (!name) return '';
    return name.toUpperCase().replace(/\s+/g, '').replace(/AND/g, 'AND');
  };

  // Helper function to zoom to a state
  const zoomToState = (stateName) => {
    if (!stateName) return;
    
    // Use district GeoJSON to find all districts for this state for accurate bounds
    if (!districtGeoData) return;

    const normalizedStateName = normalizeStateName(stateName);

    // Find all district features for this state
    const stateFeatures = districtGeoData.features.filter(
      feature => feature.properties.NAME_1 && 
      normalizeStateName(feature.properties.NAME_1) === normalizedStateName
    );

    if (stateFeatures.length === 0) {
      console.log('No features found for state:', stateName);
      return;
    }

    console.log(`Found ${stateFeatures.length} districts for ${stateName}`);

    // Calculate bounds for all features of this state
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    stateFeatures.forEach(feature => {
      const coords = feature.geometry.coordinates;
      
      const processCoords = (coordArray) => {
        coordArray.forEach(item => {
          if (Array.isArray(item[0])) {
            processCoords(item);
          } else {
            const [lng, lat] = item;
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          }
        });
      };

      processCoords(coords);
    });

    // Calculate center and zoom
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    // Calculate appropriate zoom level based on bounds size
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);
    
    let zoom = 7;
    if (maxDiff < 1) zoom = 9;
    else if (maxDiff < 2) zoom = 8;
    else if (maxDiff < 4) zoom = 7;
    else if (maxDiff < 8) zoom = 6;
    else zoom = 5;

    setMapCenter([centerLat, centerLng]);
    setMapZoom(zoom);
  };

  // Helper function to handle village selection with auto-scroll
  const handleVillageSelect = useCallback((village) => {
    setSelectedVillage(village);
    
    // Zoom to village location if coordinates are available
    if (village.latitude && village.longitude) {
      setMapCenter([village.latitude, village.longitude]);
      setMapZoom(12);
    }
    
    // Auto-scroll to village detail section after a short delay
    setTimeout(() => {
      if (villageDetailRef.current) {
        villageDetailRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 500); // Increased delay to ensure the component has fully rendered
  }, []);

  // Effect to scroll when selectedVillage changes (backup scroll trigger)
  useEffect(() => {
    if (selectedVillage && villageDetailRef.current) {
      // Additional scroll trigger with longer delay
      const scrollTimer = setTimeout(() => {
        villageDetailRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 800);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [selectedVillage]);

  // Fetch states data from API (with year parameter and role-based filtering)
  useEffect(() => {
    const fetchStatesData = async () => {
      try {
        const userData = localStorage.getItem('user');
        const userRole = userData ? JSON.parse(userData).role : 'farmer';
        
        let url = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/states?year=${selectedYear}`;
        
        // State admins see only their state
        if (userRole === 'state' && userState) {
          url += `&state=${encodeURIComponent(userState)}`;
        }
        
        const response = await fetch(url);
        const data = await response.json();
        console.log('States data loaded:', data.states.length, 'states for year', selectedYear, userRole === 'state' ? `(filtered for ${userState})` : '');
        setStatesData(data.states);
      } catch (error) {
        console.error('Error fetching states data:', error);
      }
    };
    fetchStatesData();
  }, [selectedYear, userState]);

  // Fetch summary data (with year parameter and role-based filtering)
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const userData = localStorage.getItem('user');
        const userRole = userData ? JSON.parse(userData).role : 'farmer';
        
        let url = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/summary?year=${selectedYear}`;
        
        // State admins get state-specific summary
        if (userRole === 'state' && userState) {
          url += `&state=${encodeURIComponent(userState)}`;
        }
        // District admins get district-specific summary
        else if (userRole === 'district' && userState && userDistrict) {
          url += `&state=${encodeURIComponent(userState)}&district=${encodeURIComponent(userDistrict)}`;
        }
        
        const response = await fetch(url);
        const data = await response.json();
        setSummaryData(data.summary);
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    };
    fetchSummary();
  }, [selectedYear, userState, userDistrict]);

  // Fetch state data when a state is selected (with year parameter)
  useEffect(() => {
    if (selectedState) {
      const fetchStateData = async () => {
        try {
          console.log('Fetching data for state:', selectedState, 'year:', selectedYear);
          const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/state/${encodeURIComponent(selectedState)}?year=${selectedYear}`);
          const data = await response.json();
          console.log('State data loaded:', data.name, 'with', data.districts.length, 'districts');
          setCurrentStateData(data);
        } catch (error) {
          console.error('Error fetching state data:', error);
        }
      };
      fetchStateData();
    } else {
      setCurrentStateData(null);
    }
  }, [selectedState, selectedYear]);

  // Load both state-level and district-level GeoJSON data
  // Merge them: use districts by default, but add Ladakh from state-level GeoJSON
  useEffect(() => {
    const loadGeoData = async () => {
      try {
        // Small delay to let the UI render first
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Load state-level GeoJSON (complete boundaries including full Ladakh)
        const stateResponse = await fetch('/india.geojson');
        const stateData = await stateResponse.json();
        console.log('State GeoJSON loaded:', stateData.features.length, 'states/UTs');
        
        // Load district-level GeoJSON (for detailed district view)
        const districtResponse = await fetch('/india-districts.geojson');
        const districtData = await districtResponse.json();
        console.log('District GeoJSON loaded:', districtData.features.length, 'districts');
        
        // Defer setting state to next tick
        setTimeout(() => {
          setStateGeoData(stateData);
          setDistrictGeoData(districtData);
          
          // Create merged GeoJSON: districts + Ladakh from state-level
          // Find Ladakh in state-level data
          const ladakhFeature = stateData.features.find(
            f => f.properties.st_nm && f.properties.st_nm.toUpperCase().includes('LADAKH')
          );
          
          // Find Jammu & Kashmir in state-level data (also might be missing districts)
          const jkFeature = stateData.features.find(
            f => f.properties.st_nm && 
            (f.properties.st_nm.toUpperCase().includes('JAMMU') || 
             f.properties.st_nm.toUpperCase() === 'JAMMU AND KASHMIR')
          );
          
          // Create merged feature collection
          const mergedFeatures = [...districtData.features];
          
          if (ladakhFeature) {
            console.log('Adding Ladakh from state-level GeoJSON');
            mergedFeatures.push(ladakhFeature);
          }
          
          if (jkFeature) {
            // Check if J&K districts exist in district data
            const jkDistricts = districtData.features.filter(
              f => f.properties.NAME_1 && 
              (f.properties.NAME_1.toUpperCase().includes('JAMMU') ||
               f.properties.NAME_1.toUpperCase() === 'JAMMU AND KASHMIR')
            );
            
            if (jkDistricts.length === 0) {
              console.log('Adding Jammu & Kashmir from state-level GeoJSON');
              mergedFeatures.push(jkFeature);
            }
          }
          
          const mergedGeoData = {
            type: 'FeatureCollection',
            features: mergedFeatures
          };
          
          console.log('Merged GeoJSON created:', mergedGeoData.features.length, 'features');
          setGeoData(mergedGeoData);
        }, 0);
      } catch (error) {
        console.error('Error loading GeoJSON:', error);
        setLoading(false);
      }
    };
    loadGeoData();
  }, []);

  // Load district name mapping
  useEffect(() => {
    let isMounted = true;
    
    const loadMapping = async () => {
      try {
        const response = await fetch('/districtMapping.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const mapping = await response.json();
        
        if (isMounted) {
          setDistrictMapping(mapping);
          console.log('District mapping loaded:', Object.keys(mapping.districtNameMapping).length, 'mappings');
        }
      } catch (error) {
        console.error('Error loading district mapping:', error);
        // Set empty mapping to allow the app to continue
        if (isMounted) {
          setDistrictMapping({ districtNameMapping: {}, stateNameMapping: {} });
        }
      }
    };
    
    loadMapping();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Load state-level village summary for aggregation
  useEffect(() => {
    let isMounted = true;
    
    const loadStateSummary = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/villages/states`);
        const result = await response.json();
        
        if (isMounted && result.success) {
          // Convert API format to our state aggregation format
          const stateStats = {};
          Object.entries(result.data.states).forEach(([stateName, stateData]) => {
            stateStats[stateName] = {
              count: stateData.totalVillages,
              avgWaterLevel: stateData.avgWaterLevel,
              safe: stateData.categories['Safe'] || 0,
              critical: (stateData.categories['Critical'] || 0) + (stateData.categories['Over Exploited'] || 0)
            };
          });
          
          setVillageDataByState(stateStats);
          console.log('State-level village summary loaded:', Object.keys(stateStats).length, 'states');
        }
      } catch (error) {
        console.error('Error loading state village summary:', error);
      }
    };
    
    loadStateSummary();
    
    return () => {
      isMounted = false;
    };
  }, []); // Only run once on mount

  // Load district-level village summary when a state is selected
  useEffect(() => {
    let isMounted = true;
    
    if (!selectedState) {
      setVillageDataByDistrict({});
      return;
    }
    
    const loadDistrictSummary = async () => {
      try {
        // Convert state name to title case for village API
        // "ANDHRA PRADESH" -> "Andhra Pradesh"
        const titleCaseState = selectedState
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/villages/districts/${encodeURIComponent(titleCaseState)}`);
        const result = await response.json();
        
        if (isMounted && result.success) {
          // Convert API format to our district aggregation format
          const districtStats = {};
          Object.entries(result.data).forEach(([districtName, districtData]) => {
            districtStats[districtName] = {
              count: districtData.totalVillages,
              avgWaterLevel: districtData.avgWaterLevel,
              safe: districtData.categories['Safe'] || 0,
              critical: (districtData.categories['Critical'] || 0) + (districtData.categories['Over Exploited'] || 0)
            };
          });
          
          setVillageDataByDistrict(districtStats);
          console.log('District-level village summary loaded for', titleCaseState, ':', Object.keys(districtStats).length, 'districts');
        } else {
          console.error('Failed to load district summary:', result.error || 'Unknown error');
          setVillageDataByDistrict({});
        }
      } catch (error) {
        console.error('Error loading district village summary:', error);
        setVillageDataByDistrict({});
      }
    };
    
    loadDistrictSummary();
    
    return () => {
      isMounted = false;
    };
  }, [selectedState]);

  // Load village data when a district is selected (with year selection)
  useEffect(() => {
    let isMounted = true;
    
    if (!selectedState || !selectedDistrict) {
      setFilteredVillageData([]);
      setVillageData([]);
      return;
    }
    
    const loadDistrictVillages = async () => {
      try {
        // Convert state name to title case for village API
        const titleCaseState = selectedState
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Use the new year-specific endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/villages/year/${villageDataYear}/${encodeURIComponent(titleCaseState)}/${encodeURIComponent(selectedDistrict)}`
        );
        const result = await response.json();
        
        if (isMounted && result.success) {
          const villages = result.data.villages || [];
          setVillageData(villages);
          setFilteredVillageData(villages);
          console.log('Village data loaded for', selectedDistrict, 'year', villageDataYear, ':', villages.length, 'villages');
        } else {
          console.error('Failed to load village data:', result.error || 'Unknown error');
          setVillageData([]);
          setFilteredVillageData([]);
        }
      } catch (error) {
        console.error('Error loading district villages:', error);
        setVillageData([]);
        setFilteredVillageData([]);
      }
    };
    
    loadDistrictVillages();
    
    return () => {
      isMounted = false;
    };
  }, [selectedState, selectedDistrict, villageDataYear]);

  // Callback for district label click - memoized to prevent re-renders
  const handleDistrictLabelClick = useCallback((districtName, center) => {
    console.log('District label clicked:', districtName);
    // Find matching district in current state data
    if (currentStateData && currentStateData.districts) {
      const district = currentStateData.districts.find(d => 
        d.name.toUpperCase().includes(districtName.toUpperCase()) ||
        districtName.toUpperCase().includes(d.name.toUpperCase())
      );
      if (district) {
        setSelectedDistrict(district.name);
        setSelectedLocation(`${district.name}, ${selectedState}`);
        setMapCenter(center);
        setMapZoom(9);
      }
    }
  }, [currentStateData, selectedState]);

  // Load all district data from JSON file for immediate district coloring (with year parameter)
  // Defer loading to prevent blocking
  useEffect(() => {
    const loadAllDistrictsData = async () => {
      try {
        // Small delay to prevent blocking
        await new Promise(resolve => setTimeout(resolve, 150));
        
        const yearConfig = availableYears.find(y => y.value === selectedYear);
        const dataFile = yearConfig ? yearConfig.file : 'groundwater-data.json';
        
        const response = await fetch(`/${dataFile}`);
        const data = await response.json();
        
        // Defer processing to next tick
        setTimeout(() => {
          // Create a map of state name -> districts for quick lookup
          const districtMap = {};
          data.states.forEach(state => {
            districtMap[state.name.toUpperCase()] = state.districts;
          });
          
          setAllDistrictsData(districtMap);
          console.log('All districts data loaded for', Object.keys(districtMap).length, 'states for year', selectedYear);
        }, 0);
      } catch (error) {
        console.error('Error loading all districts data:', error);
      }
    };
    loadAllDistrictsData();
  }, [selectedYear]);

  // Fetch comparison data when in comparison mode
  // Defer to prevent blocking
  useEffect(() => {
    if (comparisonMode) {
      const fetchComparisonData = async () => {
        try {
          setLoading(true);
          
          // Small delay to let UI update first
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // ALWAYS fetch ALL district-level comparisons for the entire country
          // We'll fetch state-level for the table, but district-level for the map
          const stateParam = selectedState ? `&state=${encodeURIComponent(selectedState)}` : '';
          const url = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/compare?year1=${comparisonYear1}&year2=${comparisonYear2}${stateParam}&includeAllDistricts=true`;
          console.log('Fetching comparison data from:', url);
          const response = await fetch(url);
          const data = await response.json();
          
          // Defer state update to next tick
          setTimeout(() => {
            setComparisonData(data);
            console.log('Comparison data loaded:', {
              year1: data.year1,
              year2: data.year2,
              statesCount: data.states?.length || 0,
              districtsCount: data.districts?.length || 0,
              allDistrictsCount: data.allDistricts?.length || 0,
              summary: data.summary
            });
            setLoading(false);
          }, 0);
        } catch (error) {
          console.error('Error fetching comparison data:', error);
          setLoading(false);
        }
      };
      fetchComparisonData();
    }
  }, [comparisonMode, comparisonYear1, comparisonYear2, selectedState]);

  // Helper function to match district names (handles variations) - memoized
  const findDistrictMatch = useCallback((geoDistrictName, districts) => {
    if (!geoDistrictName || !districts) return null;
    
    const geoNameUpper = geoDistrictName.toUpperCase().trim();
    
    // Check if there's a mapping for this district
    if (districtMapping && districtMapping.districtNameMapping) {
      const mappedName = districtMapping.districtNameMapping[geoDistrictName];
      if (mappedName) {
        const match = districts.find(d => d.name && d.name.toUpperCase().trim() === mappedName.toUpperCase().trim());
        if (match) return match;
      }
    }
    
    // Try exact match
    let match = districts.find(d => d.name && d.name.toUpperCase().trim() === geoNameUpper);
    if (match) return match;
    
    // Try partial match (contains)
    match = districts.find(d => {
      const dataNameUpper = d.name.toUpperCase().trim();
      return dataNameUpper.includes(geoNameUpper) || geoNameUpper.includes(dataNameUpper);
    });
    if (match) return match;
    
    // Try matching without common suffixes/prefixes
    const cleanGeoName = geoNameUpper.replace(/\s+(DISTRICT|DIST|NORTH|SOUTH|EAST|WEST)$/g, '');
    match = districts.find(d => {
      const cleanDataName = d.name.toUpperCase().trim().replace(/\s+(DISTRICT|DIST|NORTH|SOUTH|EAST|WEST)$/g, '');
      return cleanDataName === cleanGeoName || 
             cleanDataName.includes(cleanGeoName) || 
             cleanGeoName.includes(cleanDataName);
    });
    
    return match;
  }, [districtMapping]);

  // Check if all required data is loaded
  useEffect(() => {
    if (stateGeoData && districtGeoData && Object.keys(allDistrictsData).length > 0 && districtMapping) {
      setDataLoaded(true);
      setLoading(false);
      console.log('All data loaded and ready for rendering');
    }
  }, [stateGeoData, districtGeoData, allDistrictsData, districtMapping]);

  // Filter GeoJSON based on state selection
  useEffect(() => {
    if (!stateGeoData || !districtGeoData) return;
    
    if (selectedState) {
      // When a state is selected, show only districts for that state
      const normalizedSelectedState = normalizeStateName(selectedState);
      
      // Check if this is Ladakh or J&K (which might only have state-level data)
      const isLadakh = normalizedSelectedState.includes('LADAKH');
      const isJK = normalizedSelectedState.includes('JAMMU') || normalizedSelectedState === 'JAMMUANDKASHMIR';
      
      if (isLadakh || isJK) {
        // For Ladakh/J&K, try to find districts first
        const stateDistricts = districtGeoData.features.filter(
          feature => feature.properties.NAME_1 && 
          normalizeStateName(feature.properties.NAME_1) === normalizedSelectedState
        );
        
        if (stateDistricts.length > 0) {
          console.log(`Found ${stateDistricts.length} districts for ${selectedState}`);
          setGeoData({
            type: 'FeatureCollection',
            features: stateDistricts
          });
        } else {
          // No districts found, use state-level feature
          const stateFeature = stateGeoData.features.find(
            f => f.properties.st_nm && 
            normalizeStateName(f.properties.st_nm) === normalizedSelectedState
          );
          
          if (stateFeature) {
            console.log(`Using state-level boundary for ${selectedState}`);
            setGeoData({
              type: 'FeatureCollection',
              features: [stateFeature]
            });
          }
        }
      } else {
        // For other states, show districts
        const stateDistricts = districtGeoData.features.filter(
          feature => feature.properties.NAME_1 && 
          normalizeStateName(feature.properties.NAME_1) === normalizedSelectedState
        );
        
        if (stateDistricts.length > 0) {
          console.log(`Showing ${stateDistricts.length} districts for ${selectedState}`);
          setGeoData({
            type: 'FeatureCollection',
            features: stateDistricts
          });
        }
      }
    } else {
      // When no state is selected, show merged view (districts + Ladakh)
      // Recreate the merged data
      const ladakhFeature = stateGeoData.features.find(
        f => f.properties.st_nm && f.properties.st_nm.toUpperCase().includes('LADAKH')
      );
      
      const jkFeature = stateGeoData.features.find(
        f => f.properties.st_nm && 
        (f.properties.st_nm.toUpperCase().includes('JAMMU') || 
         f.properties.st_nm.toUpperCase() === 'JAMMU AND KASHMIR')
      );
      
      const mergedFeatures = [...districtGeoData.features];
      
      if (ladakhFeature) {
        mergedFeatures.push(ladakhFeature);
      }
      
      if (jkFeature) {
        const jkDistricts = districtGeoData.features.filter(
          f => f.properties.NAME_1 && 
          (f.properties.NAME_1.toUpperCase().includes('JAMMU') ||
           f.properties.NAME_1.toUpperCase() === 'JAMMU AND KASHMIR')
        );
        
        if (jkDistricts.length === 0) {
          mergedFeatures.push(jkFeature);
        }
      }
      
      console.log('Showing merged view with all districts + Ladakh');
      setGeoData({
        type: 'FeatureCollection',
        features: mergedFeatures
      });
    }
  }, [selectedState, stateGeoData, districtGeoData]);

  // Pre-calculate colors for all features when data changes
  // Use setTimeout to defer heavy computation and prevent UI blocking
  useEffect(() => {
    if (!geoData || !geoData.features) return;
    
    // Defer color calculation to prevent blocking the main thread
    const timeoutId = setTimeout(() => {
      const colorMap = {};

    geoData.features.forEach(feature => {
      if (!feature || !feature.properties) return;

      // Handle both state-level (st_nm) and district-level (NAME_1, NAME_2) properties
      const stateName = feature.properties.st_nm || feature.properties.NAME_1;
      const districtName = feature.properties.NAME_2;
      
      if (!stateName) return;

      // Create a unique key for this feature
      const featureKey = `${normalizeStateName(stateName)}-${districtName ? normalizeStateName(districtName) : 'STATE'}`;
      const normalizedStateName = normalizeStateName(stateName);

      // ============================================================
      // COMPARISON MODE: ALWAYS show DISTRICT-level comparison
      // ============================================================
      if (comparisonMode && comparisonData) {
        
        // Use allDistricts for map rendering (contains ALL districts across India)
        // Use districts for table (filtered by selected state)
        const districtDataSource = selectedState 
          ? (comparisonData.districts || [])
          : (comparisonData.allDistricts || []);
        
        if (districtName && districtDataSource.length > 0) {
          // This is a district feature - look for district comparison data
          const normalizedDistrictName = normalizeStateName(districtName);
          
          // Find this specific district in the comparison data
          // Match by both district name AND state name for accuracy
          const districtComparison = districtDataSource.find(d => {
            const normalizedComparisonName = normalizeStateName(d.name);
            const normalizedComparisonState = normalizeStateName(d.stateName);
            
            const nameMatch = normalizedComparisonName === normalizedDistrictName || 
                             normalizedComparisonName.includes(normalizedDistrictName) || 
                             normalizedDistrictName.includes(normalizedComparisonName);
            
            const stateMatch = normalizedComparisonState === normalizedStateName;
            
            return nameMatch && stateMatch;
          });
          
          if (districtComparison) {
            // District found in BOTH years - show its comparison color
            colorMap[featureKey] = comparisonColors[districtComparison.classification] || comparisonColors['no_data'];
            
            // Debug logging for specific districts
            if (stateName.toUpperCase().includes('HARYANA') && Math.random() < 0.05) {
              console.log(`District: ${districtName} (${stateName}) -> ${districtComparison.classification}`);
            }
          } else {
            // District NOT found in both years - show grey
            colorMap[featureKey] = comparisonColors['no_data'];
          }
        } else if (!districtName) {
          // This is a state-level feature (no district name)
          // Show grey since we're doing district-level comparison
          colorMap[featureKey] = comparisonColors['no_data'];
        } else {
          // No district data available - show grey
          colorMap[featureKey] = comparisonColors['no_data'];
        }
      } 
      // ============================================================
      // NORMAL MODE: Show category for a single year
      // ============================================================
      else {
        // CASE 1: This is a district feature
        if (districtName && allDistrictsData && Object.keys(allDistrictsData).length > 0) {
          // Find the state in our data
          const stateKey = Object.keys(allDistrictsData).find(key => 
            normalizeStateName(key) === normalizedStateName
          );
          
          if (stateKey) {
            const stateDistrictsData = allDistrictsData[stateKey];
            const districtInfo = findDistrictMatch(districtName, stateDistrictsData);
            
            if (districtInfo && districtInfo.category) {
              // District found with category - show its color
              colorMap[featureKey] = categoryColors[districtInfo.category] || categoryColors['No Data'];
            } else {
              // District not found in data - show grey
              colorMap[featureKey] = categoryColors['No Data'];
            }
          } else {
            // State not found in data - show grey
            colorMap[featureKey] = categoryColors['No Data'];
          }
        }
        // CASE 2: This is a state-level feature (no district name)
        else if (!districtName) {
          if (statesData && statesData.length > 0) {
            const stateInfo = statesData.find(s => 
              s.name && normalizeStateName(s.name) === normalizedStateName
            );
            if (stateInfo && stateInfo.category) {
              // State found with category - show its color
              colorMap[featureKey] = categoryColors[stateInfo.category] || categoryColors['No Data'];
            } else {
              // State not found in data - show grey
              colorMap[featureKey] = categoryColors['No Data'];
            }
          } else {
            colorMap[featureKey] = categoryColors['No Data'];
          }
        } else {
          // Fallback - show grey
          colorMap[featureKey] = categoryColors['No Data'];
        }
      }
    });

    setFeatureColorMap(colorMap);
    }, 0); // Defer to next tick to prevent blocking

    // Cleanup function to cancel pending calculation if component unmounts or dependencies change
    return () => {
      clearTimeout(timeoutId);
    };
  }, [geoData, comparisonMode, comparisonData, selectedState, allDistrictsData, statesData, findDistrictMatch]);

  // Get color for a feature based on pre-calculated color map - memoized
  const getFeatureColor = useCallback((feature) => {
    if (!feature || !feature.properties) {
      return comparisonMode ? comparisonColors['no_data'] : categoryColors['No Data'];
    }
    
    // Handle both state-level (st_nm) and district-level (NAME_1, NAME_2) properties
    const stateName = feature.properties.st_nm || feature.properties.NAME_1;
    const districtName = feature.properties.NAME_2;
    
    if (!stateName) {
      return comparisonMode ? comparisonColors['no_data'] : categoryColors['No Data'];
    }
    
    // PRIORITY 1: Check if this is the user's district (highest priority)
    if (highlightUserLocation && userState && userDistrict && districtName) {
      const normalizedStateName = normalizeStateName(stateName);
      const normalizedUserState = normalizeStateName(userState);
      const normalizedDistrictName = normalizeStateName(districtName);
      const normalizedUserDistrict = normalizeStateName(userDistrict);
      
      // Check if this is the user's district
      if (normalizedStateName === normalizedUserState && 
          (normalizedDistrictName === normalizedUserDistrict ||
           normalizedDistrictName.includes(normalizedUserDistrict) ||
           normalizedUserDistrict.includes(normalizedDistrictName))) {
        return '#FF1744'; // Bright red for user's district
      }
      
      // If highlighting user location, show all other areas in light gray
      return '#E0E0E0';
    }
    
    // Create the same key used during pre-calculation
    const featureKey = `${normalizeStateName(stateName)}-${districtName ? normalizeStateName(districtName) : 'STATE'}`;
    
    // Look up the pre-calculated color
    const color = featureColorMap[featureKey];
    
    // Return the pre-calculated color or default to No Data
    return color || (comparisonMode ? comparisonColors['no_data'] : categoryColors['No Data']);
  }, [featureColorMap, comparisonMode, comparisonColors, categoryColors, highlightUserLocation, userState, userDistrict]);

  // Style function for GeoJSON - memoized
  const style = useCallback((feature) => {
    // Handle both state-level (st_nm) and district-level (NAME_1, NAME_2) properties
    const stateName = feature?.properties?.st_nm || feature?.properties?.NAME_1;
    const districtName = feature?.properties?.NAME_2;
    
    const isSelectedState = selectedState && stateName && 
                           normalizeStateName(selectedState) === normalizeStateName(stateName);
    
    // Check if this is the selected district
    const isSelectedDistrict = selectedDistrict && districtName && isSelectedState &&
                               normalizeStateName(districtName).includes(normalizeStateName(selectedDistrict));
    
    // Check if this is the user's district (for special highlighting)
    const isUserDistrict = highlightUserLocation && userState && userDistrict && 
                          stateName && districtName &&
                          normalizeStateName(stateName) === normalizeStateName(userState) &&
                          (normalizeStateName(districtName) === normalizeStateName(userDistrict) ||
                           normalizeStateName(districtName).includes(normalizeStateName(userDistrict)) ||
                           normalizeStateName(userDistrict).includes(normalizeStateName(districtName)));
    
    // Check if this feature should be non-interactive (outside selected state)
    const isNonInteractive = selectedState && !isSelectedState;
    
    return {
      fillColor: getFeatureColor(feature),
      weight: isUserDistrict ? 5 : (isSelectedDistrict ? 4 : (isSelectedState ? 2 : 1)),  // Thickest for user district
      opacity: isNonInteractive ? 0.3 : 1, // Reduce opacity for non-interactive features
      color: isUserDistrict ? '#FF1744' : (isSelectedDistrict ? '#FF00FF' : (isSelectedState ? '#000' : '#666')),  // Red for user district
      fillOpacity: isNonInteractive ? 0.2 : (isUserDistrict ? 0.95 : (isSelectedDistrict ? 0.9 : 0.7)), // Reduce fill opacity for non-interactive
      className: isNonInteractive ? 'non-interactive-feature' : '' // Add class for CSS styling
    };
  }, [selectedState, selectedDistrict, getFeatureColor, highlightUserLocation, userState, userDistrict]);

  // Handle feature click - memoized
  const onEachFeature = useCallback((feature, layer) => {
    if (!feature || !feature.properties) return;
    
    // Handle both state-level (st_nm) and district-level (NAME_1, NAME_2) properties
    const stateName = feature.properties.st_nm || feature.properties.NAME_1;
    const districtName = feature.properties.NAME_2;
    
    if (!stateName) return;
    
    // Check if this feature should be interactive
    const isInSelectedState = selectedState && 
                              normalizeStateName(stateName) === normalizeStateName(selectedState);
    const shouldBeInteractive = !selectedState || isInSelectedState;
    
    layer.on({
      mouseover: (e) => {
        // Only apply hover effect if this feature should be interactive
        if (!shouldBeInteractive) return;
        
        const layer = e.target;
        // Get current fill color dynamically
        const currentStyle = style(feature);
        // Keep the original fill color, just change border
        if (currentStyle && currentStyle.fillColor) {
          layer.setStyle({
            weight: 3,
            color: '#000',
            fillOpacity: 0.9,
            fillColor: currentStyle.fillColor // Preserve the current color
          });
        }
      },
      mouseout: (e) => {
        const layer = e.target;
        // Restore the current style dynamically
        const currentStyle = style(feature);
        if (currentStyle) {
          layer.setStyle(currentStyle);
        }
      },
      click: (e) => {
        // Only allow clicks on interactive features
        if (!shouldBeInteractive && selectedState) return;
        
        e.originalEvent.stopPropagation();
        
        if (!selectedState || normalizeStateName(selectedState) !== normalizeStateName(stateName)) {
          // First click: Select state - find the matching state name from our data
          const matchingState = statesData.find(s => 
            normalizeStateName(s.name) === normalizeStateName(stateName)
          );
          const stateNameToUse = matchingState ? matchingState.name : stateName;
          
          console.log('Selecting state:', stateNameToUse);
          setSelectedState(stateNameToUse);
          setSelectedDistrict(null);
          setSelectedLocation(stateNameToUse);
          // Zoom to state bounds
          const bounds = e.target.getBounds();
          setMapCenter([bounds.getCenter().lat, bounds.getCenter().lng]);
          setMapZoom(7);
        } else if (selectedState && normalizeStateName(selectedState) === normalizeStateName(stateName) && districtName) {
          // Second click: Select district within the same state
          console.log('Selecting district:', districtName, 'in', stateName);
          
          // Find the actual district name from our data
          if (currentStateData && currentStateData.districts) {
            const districtInfo = findDistrictMatch(districtName, currentStateData.districts);
            if (districtInfo) {
              setSelectedDistrict(districtInfo.name); // Use the matched name from our data
              setSelectedLocation(`${districtInfo.name}, ${selectedState}`);
            } else {
              setSelectedDistrict(districtName);
              setSelectedLocation(`${districtName}, ${selectedState}`);
            }
          } else {
            setSelectedDistrict(districtName);
            setSelectedLocation(`${districtName}, ${selectedState}`);
          }
          
          // Zoom to district
          const bounds = e.target.getBounds();
          setMapCenter([bounds.getCenter().lat, bounds.getCenter().lng]);
          setMapZoom(9);
        }
      }
    });

    // Only bind tooltip if this feature should be interactive
    if (!shouldBeInteractive) return;

    // Tooltip - show district name and data
    let tooltipContent = `<strong>${districtName || stateName}</strong><br/>`;
    
    if (comparisonMode && comparisonData) {
      // Comparison mode tooltip - ALWAYS show district-level data
      if (districtName) {
        tooltipContent += `<em>${stateName}</em><br/>`;
        
        // Use the same data source as the map coloring
        const districtDataSource = selectedState 
          ? (comparisonData.districts || [])
          : (comparisonData.allDistricts || []);
        
        const normalizedDistrictName = normalizeStateName(districtName);
        const normalizedStateName = normalizeStateName(stateName);
        
        // Find district by matching BOTH name AND state
        const districtComparison = districtDataSource.find(d => {
          const normalizedComparisonName = normalizeStateName(d.name);
          const normalizedComparisonState = normalizeStateName(d.stateName);
          
          const nameMatch = normalizedComparisonName === normalizedDistrictName || 
                           normalizedComparisonName.includes(normalizedDistrictName) || 
                           normalizedDistrictName.includes(normalizedComparisonName);
          
          const stateMatch = normalizedComparisonState === normalizedStateName;
          
          return nameMatch && stateMatch;
        });
        
        if (districtComparison) {
          const change = districtComparison.changes.extractionRatio;
          const changeText = change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
          const changeColor = change > 0 ? '#d32f2f' : '#2e7d32';
          tooltipContent += `<span style="color: ${changeColor};">Change: ${changeText}</span><br/>`;
          tooltipContent += `Status: ${districtComparison.classification.replace(/_/g, ' ')}<br/>`;
          tooltipContent += `Year 1: ${districtComparison.data1.extractionRatio.toFixed(1)}%<br/>`;
          tooltipContent += `Year 2: ${districtComparison.data2.extractionRatio.toFixed(1)}%<br/>`;
          tooltipContent += `<em>Click to view details</em>`;
        } else {
          tooltipContent += `<span style="color: #999;">No comparison data</span><br/>`;
          tooltipContent += `<em style="color: #999;">District not in both years</em>`;
        }
      } else {
        // State-level feature (no district name) - show state info
        const stateComparison = comparisonData.states.find(s => 
          normalizeStateName(s.name) === normalizeStateName(stateName)
        );
        if (stateComparison) {
          const change = stateComparison.changes.extractionRatio;
          const changeText = change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
          const changeColor = change > 0 ? '#d32f2f' : '#2e7d32';
          tooltipContent += `<span style="color: ${changeColor};">State Change: ${changeText}</span><br/>`;
          tooltipContent += `Status: ${stateComparison.classification.replace(/_/g, ' ')}<br/>`;
          tooltipContent += `<em>Click to view districts</em>`;
        } else {
          tooltipContent += `<span style="color: #999;">No comparison data</span>`;
        }
      }
    } else {
      // Normal mode tooltip
      if (districtName) {
        tooltipContent += `<em>${stateName}</em><br/>`;
        
        // Try to find district data
        const normalizedStateName = normalizeStateName(stateName);
        const stateKey = Object.keys(allDistrictsData).find(key => 
          normalizeStateName(key) === normalizedStateName
        );
        
        if (stateKey) {
          const stateDistrictsData = allDistrictsData[stateKey];
          const districtInfo = findDistrictMatch(districtName, stateDistrictsData);
          
          if (districtInfo) {
            tooltipContent += `Category: ${districtInfo.category}<br/>`;
            tooltipContent += `Rainfall: ${districtInfo.rainfall.toFixed(2)} mm<br/>`;
            tooltipContent += `<em>Click to view details</em>`;
          } else {
            tooltipContent += `<span style="color: #999;">Category: No Data</span><br/>`;
            tooltipContent += `<em style="color: #999;">Data not available for this district</em>`;
          }
        } else {
          tooltipContent += `<span style="color: #999;">Category: No Data</span><br/>`;
          tooltipContent += `<em style="color: #999;">Data not available for this state</em>`;
        }
      } else {
        // State-level tooltip
        const stateInfo = statesData.find(s => 
          normalizeStateName(s.name) === normalizeStateName(stateName)
        );
        if (stateInfo) {
          tooltipContent += `Category: ${stateInfo.category}<br/>`;
          tooltipContent += `Rainfall: ${stateInfo.rainfall.toFixed(2)} mm`;
        } else {
          tooltipContent += `<span style="color: #999;">Category: No Data</span><br/>`;
          tooltipContent += `<em style="color: #999;">Data not available</em>`;
        }
      }
    }
    
    layer.bindTooltip(tooltipContent, { sticky: true });
  }, [comparisonMode, comparisonData, selectedState, allDistrictsData, statesData, selectedDistrict, currentStateData, style, findDistrictMatch]);

  // Get current data based on selection
  const getCurrentData = () => {
    if (selectedState === null) {
      // India level - use summary data
      if (summaryData) {
        return {
          location: 'INDIA',
          rainfall: summaryData.rainfall.toFixed(2),
          extractable: summaryData.annualExtractableGroundWaterResources.toFixed(2),
          extraction: summaryData.groundWaterExtraction.toFixed(2),
          recharge: (summaryData.annualExtractableGroundWaterResources * 0.15).toFixed(2),
          discharge: (summaryData.annualExtractableGroundWaterResources * 0.05).toFixed(2)
        };
      }
    } else if (selectedDistrict === null) {
      // State level
      if (currentStateData) {
        return {
          location: selectedState,
          rainfall: currentStateData.rainfall.toFixed(2),
          extractable: currentStateData.annualExtractableGroundWaterResources.toFixed(2),
          extraction: currentStateData.groundWaterExtraction.toFixed(2),
          recharge: (currentStateData.annualExtractableGroundWaterResources * 0.15).toFixed(2),
          discharge: (currentStateData.annualExtractableGroundWaterResources * 0.05).toFixed(2),
          category: currentStateData.category
        };
      }
    } else {
      // District level
      if (currentStateData && currentStateData.districts) {
        // Use findDistrictMatch to handle name variations
        const districtInfo = findDistrictMatch(selectedDistrict, currentStateData.districts);
        if (districtInfo) {
          return {
            location: `${selectedDistrict}, ${selectedState}`,
            rainfall: districtInfo.rainfall.toFixed(2),
            extractable: districtInfo.annualExtractableGroundWaterResources.toFixed(2),
            extraction: districtInfo.groundWaterExtraction.toFixed(2),
            recharge: (districtInfo.annualExtractableGroundWaterResources * 0.15).toFixed(2),
            discharge: (districtInfo.annualExtractableGroundWaterResources * 0.05).toFixed(2),
            category: districtInfo.category
          };
        } else {
          console.warn('District not found in groundwater data:', selectedDistrict);
        }
      }
    }
    return null;
  };

  // Reset to India view
  const resetView = () => {
    setSelectedState(null);
    setSelectedDistrict(null);
    setSelectedLocation('INDIA');
    setMapCenter([23.5, 82]);
    setMapZoom(4.4);
    
    // Force recreation of merged GeoJSON (districts + Ladakh)
    if (stateGeoData && districtGeoData) {
      const ladakhFeature = stateGeoData.features.find(
        f => f.properties.st_nm && f.properties.st_nm.toUpperCase().includes('LADAKH')
      );
      
      const jkFeature = stateGeoData.features.find(
        f => f.properties.st_nm && 
        (f.properties.st_nm.toUpperCase().includes('JAMMU') || 
         f.properties.st_nm.toUpperCase() === 'JAMMU AND KASHMIR')
      );
      
      const mergedFeatures = [...districtGeoData.features];
      
      if (ladakhFeature) {
        mergedFeatures.push(ladakhFeature);
      }
      
      if (jkFeature) {
        const jkDistricts = districtGeoData.features.filter(
          f => f.properties.NAME_1 && 
          (f.properties.NAME_1.toUpperCase().includes('JAMMU') ||
           f.properties.NAME_1.toUpperCase() === 'JAMMU AND KASHMIR')
        );
        
        if (jkDistricts.length === 0) {
          mergedFeatures.push(jkFeature);
        }
      }
      
      console.log('Reset: Showing merged view with all districts + Ladakh');
      setGeoData({
        type: 'FeatureCollection',
        features: mergedFeatures
      });
    }
    
    // If in comparison mode, this will trigger a refetch of comparison data without state filter
  };

  // Handle year change
  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
    // Reset selections when year changes
    setSelectedState(null);
    setSelectedDistrict(null);
    setSelectedLocation('INDIA');
    setMapCenter([23.5, 82]);
    setMapZoom(4.6);
    setLoading(true);
  };

  const currentData = getCurrentData();
  
  // Memoize table data to prevent unnecessary recalculations
  const tableData = useMemo(() => {
    return selectedState === null ? statesData : (currentStateData?.districts || []);
  }, [selectedState, statesData, currentStateData]);

  // Generate insightful summary for the selected region
  const generateSummary = () => {
    if (!currentData) return null;

    const extractable = parseFloat(currentData.extractable);
    const extraction = parseFloat(currentData.extraction);
    const rainfall = parseFloat(currentData.rainfall);
    const extractionPercentage = extractable > 0 ? ((extraction / extractable) * 100).toFixed(1) : 0;
    
    const location = currentData.location || 'INDIA';
    const isIndia = selectedState === null;
    const isState = selectedState !== null && selectedDistrict === null;
    const isDistrict = selectedDistrict !== null;

    // Determine status and recommendations
    let status, statusIcon, statusColor, explanation, recommendations;

    if (extractionPercentage >= 100) {
      status = 'Critical Situation';
      statusIcon = '🚨';
      statusColor = '#ea2328';
      explanation = `${location} is extracting more groundwater than can be naturally replenished. This is unsustainable and poses serious risks.`;
      recommendations = [
        'Immediate water conservation measures needed',
        'Promote rainwater harvesting systems',
        'Restrict groundwater extraction for non-essential uses',
        'Implement artificial recharge projects'
      ];
    } else if (extractionPercentage >= 90) {
      status = 'High Risk Zone';
      statusIcon = '⚠️';
      statusColor = '#ff8d1a';
      explanation = `${location} is approaching critical groundwater depletion levels. Urgent action is required to prevent a water crisis.`;
      recommendations = [
        'Strengthen water conservation efforts',
        'Increase rainwater harvesting infrastructure',
        'Monitor and regulate groundwater extraction',
        'Promote water-efficient irrigation methods'
      ];
    } else if (extractionPercentage >= 70) {
      status = 'Moderate Concern';
      statusIcon = '⚡';
      statusColor = '#f9c802';
      explanation = `${location} is using a significant portion of its groundwater resources. Proactive measures can prevent future problems.`;
      recommendations = [
        'Implement water conservation programs',
        'Encourage rainwater harvesting',
        'Monitor extraction rates regularly',
        'Educate communities on sustainable water use'
      ];
    } else {
      status = 'Sustainable Level';
      statusIcon = '✅';
      statusColor = '#1fc21f';
      explanation = `${location} is managing its groundwater resources well. Current extraction levels are sustainable.`;
      recommendations = [
        'Continue monitoring groundwater levels',
        'Maintain current conservation practices',
        'Plan for future water security',
        'Share best practices with neighboring regions'
      ];
    }

    // Rainfall context
    let rainfallContext;
    if (rainfall < 500) {
      rainfallContext = 'receives very low rainfall, making groundwater conservation even more critical';
    } else if (rainfall < 1000) {
      rainfallContext = 'receives moderate rainfall, requiring balanced water management';
    } else if (rainfall < 2000) {
      rainfallContext = 'receives good rainfall, which helps recharge groundwater';
    } else {
      rainfallContext = 'receives abundant rainfall, providing good natural recharge';
    }

    return {
      status,
      statusIcon,
      statusColor,
      explanation,
      recommendations,
      rainfallContext,
      extractionPercentage,
      location,
      isIndia,
      isState,
      isDistrict
    };
  };

  const summary = generateSummary();

  return (
    <div className="groundwater-dashboard">
      {/* Header Section */}
      <div className="gw-header-section">
        <div className="gw-header-background"></div>
        <div className="gw-header-content">
          <h1 className="gw-header-title">
            <FaTint className="gw-header-icon" />
            {userDistrict ? `${userDistrict} District - Groundwater Dashboard` : 
             userState ? `${userState} State - Groundwater Dashboard` : 
             'Groundwater Dashboard'}
          </h1>
          <p className="gw-header-subtitle">
            Monitor <strong>groundwater resources</strong>, track <strong>extraction levels</strong>, 
            and assess <strong>sustainability</strong> {userDistrict ? `in ${userDistrict}, ${userState}` : 
            userState ? `across ${userState} state` : 'across India'}
          </p>
          
          {/* Comparison Mode Toggle */}
          <div className="gw-mode-toggle-container">
            <button 
              className={`gw-mode-toggle ${!comparisonMode ? 'active' : ''}`}
              onClick={() => {
                setComparisonMode(false);
                setComparisonData(null); // Clear comparison data
                setSelectedState(null);
                setSelectedDistrict(null);
                setSelectedLocation('INDIA');
                setMapCenter([23.5, 82]);
                setMapZoom(4.6);
              }}
            >
              Single Year View
            </button>
            <button 
              className={`gw-mode-toggle ${comparisonMode ? 'active' : ''}`}
              onClick={() => {
                setComparisonMode(true);
                setSelectedState(null);
                setSelectedDistrict(null);
                setSelectedLocation('INDIA');
                setMapCenter([23.5, 82]);
                setMapZoom(4.6);
              }}
            >
              Comparison Mode
            </button>
          </div>

          {/* Year Selector(s) */}
          {!comparisonMode ? (
            <div className="gw-year-selector-container">
              <label htmlFor="year-select" className="gw-year-label">Select Year:</label>
              <select 
                id="year-select"
                value={selectedYear} 
                onChange={handleYearChange}
                className="gw-year-select"
              >
                {availableYears.map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="gw-comparison-selector-container">
              <div className="gw-year-selector-group">
                <label htmlFor="year1-select" className="gw-year-label">Base Year:</label>
                <select 
                  id="year1-select"
                  value={comparisonYear1} 
                  onChange={(e) => setComparisonYear1(e.target.value)}
                  className="gw-year-select"
                >
                  {availableYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>
              <span className="gw-vs-text">vs</span>
              <div className="gw-year-selector-group">
                <label htmlFor="year2-select" className="gw-year-label">Compare Year:</label>
                <select 
                  id="year2-select"
                  value={comparisonYear2} 
                  onChange={(e) => setComparisonYear2(e.target.value)}
                  className="gw-year-select"
                >
                  {availableYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="gw-tab-navigation">
        <button 
          className={`gw-tab-btn ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => setActiveTab('map')}
        >
          <FaMapMarkerAlt /> Map View
        </button>
        <button 
          className={`gw-tab-btn ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          <FaChartLine /> Trend Analysis
        </button>
      </div>

      {/* Main Content */}
      <div className="gw-main-content">
        {activeTab === 'map' ? (
          <>
            {/* Map Section with Info Panel */}
            <div className="gw-map-section">
          {/* Left Column - Map and Legend */}
          <div className="gw-left-column">
            <div className="gw-map-container">
              <div className="gw-map-header">
                <div className="gw-map-header-top">
                  <h3>
                    <FaMapMarkerAlt /> Interactive Map
                    {!comparisonMode && (
                      <span className="gw-zoom-indicator" style={{
                        fontSize: '12px',
                        marginLeft: '10px',
                        padding: '4px 8px',
                        backgroundColor: currentZoom < 6 ? '#e3f2fd' : currentZoom <= 8 ? '#fff3e0' : '#e8f5e9',
                        color: currentZoom < 6 ? '#1976d2' : currentZoom <= 8 ? '#f57c00' : '#388e3c',
                        borderRadius: '4px',
                        fontWeight: 'normal'
                      }}>
                        {currentZoom < 6 ? '🗺️ State View' : currentZoom <= 8 ? '📍 District View' : '🏘️ Village View'}
                      </span>
                    )}
                  </h3>
                  <div className="gw-map-controls">
                  {/* User Location Toggle */}
                  {userState && userDistrict && (
                    <div className="gw-user-location-toggle">
                      <button
                        className={`gw-toggle-btn ${highlightUserLocation ? 'active' : ''}`}
                        onClick={() => setHighlightUserLocation(!highlightUserLocation)}
                        title={`${highlightUserLocation ? 'Hide' : 'Show'} your location: ${userDistrict}, ${userState}`}
                        style={{
                          backgroundColor: highlightUserLocation ? '#FF1744' : '#f5f5f5',
                          color: highlightUserLocation ? 'white' : '#666',
                          border: `2px solid ${highlightUserLocation ? '#FF1744' : '#ddd'}`,
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          marginRight: '10px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        📍 {highlightUserLocation ? 'Hide' : 'Show'} My Location
                      </button>
                    </div>
                  )}
                  
                  {/* Year Selector - Only show when district is selected */}
                  {selectedDistrict && (
                    <YearSelector
                      selectedYear={villageDataYear}
                      onYearChange={setVillageDataYear}
                      availableYears={[
                        { value: '2024', label: '2024' },
                        { value: '2023', label: '2023' },
                        { value: '2022', label: '2022' },
                        { value: '2021', label: '2021' }
                      ]}
                      disabled={!selectedDistrict}
                    />
                  )}
                  
                  {/* Village Map Mode Toggle - Only show when zoom > 8 and district selected */}
                  {currentZoom > 8 && selectedDistrict && filteredVillageData.length > 0 && (
                    <div className="gw-village-map-toggle">
                      <span className="gw-toggle-label">Village View:</span>
                      <button
                        className={`gw-toggle-btn ${villageMapMode === 'markers' ? 'active' : ''}`}
                        onClick={() => setVillageMapMode('markers')}
                        title="Show village markers"
                      >
                        <FaCircle size={10} /> Show Villages
                      </button>
                      <button
                        className={`gw-toggle-btn ${villageMapMode === 'off' ? 'active' : ''}`}
                        onClick={() => setVillageMapMode('off')}
                        title="Hide village markers"
                      >
                        Hide
                      </button>
                    </div>
                  )}
                  <button onClick={resetView} className="gw-reset-button">
                    Reset to India
                  </button>
                </div>
                </div>
                
                {/* Search Filter */}
                <div className="gw-search-container">
                  <GroundwaterSearchFilter
                    statesData={statesData}
                    currentStateData={currentStateData}
                    villageData={filteredVillageData}
                    allDistrictsData={allDistrictsData}
                    geoData={geoData}
                    selectedState={selectedState}
                    selectedDistrict={selectedDistrict}
                    onStateSelect={(stateName) => {
                      setSelectedState(stateName);
                      setSelectedDistrict(null);
                      setSelectedLocation(stateName);
                      zoomToState(stateName);
                    }}
                    onDistrictSelect={(districtName, center) => {
                      setSelectedDistrict(districtName);
                      setSelectedLocation(`${districtName}, ${selectedState}`);
                      // Zoom to district center if provided
                      if (center) {
                        setMapCenter(center);
                        setMapZoom(9);
                      } else {
                        // Fallback: try to find district and zoom to it
                        if (currentStateData && currentStateData.districts) {
                          const district = currentStateData.districts.find(d => d.name === districtName);
                          if (district) {
                            setMapZoom(9);
                          }
                        }
                      }
                    }}
                    onVillageSelect={handleVillageSelect}
                    onReset={resetView}
                  />
                </div>
              </div>
              
              {/* Progressive Disclosure Info */}
              {!comparisonMode && (
                <div className="gw-progressive-info">
                  <span className="gw-progressive-info-icon">💡</span>
                  <div className="gw-progressive-info-text">
                    <strong>Smart Map Navigation:</strong> {' '}
                    {currentZoom < 6 && 'Zoom in to see district-level village data'}
                    {currentZoom >= 6 && currentZoom <= 8 && 'Zoom in further to see individual villages'}
                    {currentZoom > 8 && 'Viewing individual village markers - click for details'}
                  </div>
                </div>
              )}
              
              {/* Map viewport wrapper - contains map and legend */}
              <div className="gw-map-viewport">
                {!dataLoaded ? (
                  <div className="gw-loading">
                    <div className="gw-loading-spinner"></div>
                    <p>Loading map data...</p>
                    <p style={{fontSize: '12px', marginTop: '10px'}}>
                      {stateGeoData ? '✓ State GeoJSON loaded' : '⏳ Loading state GeoJSON...'}
                      <br/>
                      {districtGeoData ? '✓ District GeoJSON loaded' : '⏳ Loading district GeoJSON...'}
                      <br/>
                      {Object.keys(allDistrictsData).length > 0 ? '✓ District data loaded' : '⏳ Loading district data...'}
                      <br/>
                      {districtMapping ? '✓ Mapping loaded' : '⏳ Loading mapping...'}
                    </p>
                  </div>
                ) : (
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    className="gw-map"
                    zoomControl={true}
                    scrollWheelZoom={true}
                    minZoom={4}
                    maxZoom={10}
                    maxBounds={[[6, 66], [38, 100]]}
                  >
                    <MapController 
                      center={mapCenter} 
                      zoom={mapZoom} 
                      onZoomChange={setCurrentZoom}
                    />
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                      attribution='© OpenStreetMap contributors, © CARTO'
                      opacity={0.4}
                    />
                    {geoData && (
                      <GeoJSON
                        key={`geojson-${comparisonMode ? 'comparison' : 'normal'}-${selectedState || 'india'}-${selectedDistrict || 'none'}-${comparisonYear1}-${comparisonYear2}-${selectedState ? 'district' : 'state'}`}
                        data={geoData}
                        style={style}
                        onEachFeature={onEachFeature}
                      />
                    )}
                    
                    {/* PROGRESSIVE DISCLOSURE - Level 2: District-level badges (when state is selected) */}
                    {!comparisonMode && selectedState && districtGeoData && Object.keys(villageDataByDistrict).length > 0 && (
                      <DistrictVillageLabel
                        key={`district-labels-${selectedState}`}
                        districtGeoData={districtGeoData}
                        villageDataByDistrict={villageDataByDistrict}
                        onDistrictClick={handleDistrictLabelClick}
                        userState={userState}
                        userDistrict={userDistrict}
                        selectedState={selectedState}
                      />
                    )}
                    
                    {/* PROGRESSIVE DISCLOSURE - Level 3: Village markers (Zoom > 8) */}
                    {!comparisonMode && currentZoom > 8 && selectedDistrict && filteredVillageData.length > 0 && villageMapMode !== 'off' && (
                      <VillageMapLayer
                        villages={filteredVillageData}
                        mode={villageMapMode === 'both' ? 'markers' : villageMapMode}
                        onVillageClick={handleVillageSelect}
                        highlightedVillage={hoveredVillage || selectedVillage}
                        categoryColors={categoryColors}
                      />
                    )}
                  </MapContainer>
                )}
                
                {/* Legend - overlaid on map */}
                <div className="gw-legend">
                  <h4>{comparisonMode ? 'Change Classification' : 'Category Legend'}</h4>
                  <div className="gw-legend-items">
                    {comparisonMode ? (
                      <>
                        <div className="gw-legend-item">
                          <div className="gw-legend-color" style={{ backgroundColor: comparisonColors['significantly_improved'] }}></div>
                          <span>Significantly Improved</span>
                        </div>
                        <div className="gw-legend-item">
                          <div className="gw-legend-color" style={{ backgroundColor: comparisonColors['improved'] }}></div>
                          <span>Improved</span>
                        </div>
                        <div className="gw-legend-item">
                          <div className="gw-legend-color" style={{ backgroundColor: comparisonColors['neutral'] }}></div>
                          <span>Neutral</span>
                        </div>
                        <div className="gw-legend-item">
                          <div className="gw-legend-color" style={{ backgroundColor: comparisonColors['degraded'] }}></div>
                          <span>Degraded</span>
                        </div>
                        <div className="gw-legend-item">
                          <div className="gw-legend-color" style={{ backgroundColor: comparisonColors['significantly_degraded'] }}></div>
                          <span>Significantly Degraded</span>
                        </div>
                        <div className="gw-legend-item">
                          <div className="gw-legend-color" style={{ backgroundColor: comparisonColors['no_data'] }}></div>
                          <span>No Data</span>
                        </div>
                      </>
                    ) : (
                      Object.entries(categoryColors).map(([category, color]) => (
                        <div key={category} className="gw-legend-item">
                          <div
                            className="gw-legend-color"
                            style={{ backgroundColor: color }}
                          ></div>
                          <span>{category}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Info Panel Only */}
          <div className="gw-right-column">
            {/* Info Panel */}
            <div className="gw-info-panel">
              <div className="gw-info-header">
                <h3>
                  <FaChartBar /> {comparisonMode ? 'Comparison Stats' : 'Statistics'}
                </h3>
                <div className="gw-location-badge">
                  <FaMapMarkerAlt />
                  {currentData?.location || 'INDIA'}
                </div>
              </div>

              {comparisonMode && comparisonData ? (
                // Comparison Stats
                <div className="gw-stats-grid">
                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">📊</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Extraction Ratio Change</div>
                      <div className={`gw-stat-value ${comparisonData.summary.changes.extractionRatio > 0 ? 'gw-negative-change' : 'gw-positive-change'}`}>
                        {comparisonData.summary.changes.extractionRatio > 0 ? '+' : ''}
                        {comparisonData.summary.changes.extractionRatio.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">🌧️</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Rainfall Change</div>
                      <div className="gw-stat-value">
                        {comparisonData.summary.changes.rainfall > 0 ? '+' : ''}
                        {comparisonData.summary.changes.rainfall.toFixed(2)} mm
                      </div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">⚡</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Extraction Change</div>
                      <div className={`gw-stat-value ${comparisonData.summary.changes.extraction > 0 ? 'gw-negative-change' : 'gw-positive-change'}`}>
                        {comparisonData.summary.changes.extraction > 0 ? '+' : ''}
                        {comparisonData.summary.changes.extraction.toFixed(2)} ham
                      </div>
                    </div>
                  </div>

                  <div className="gw-stat-card gw-category-card">
                    <div className="gw-stat-icon">🎯</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Overall Status</div>
                      <div
                        className="gw-category-badge"
                        style={{
                          backgroundColor: comparisonColors[comparisonData.summary.classification],
                          color: '#fff'
                        }}
                      >
                        {comparisonData.summary.classification.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">📅</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">{comparisonYear1} Ratio</div>
                      <div className="gw-stat-value">{comparisonData.summary.data1.extractionRatio.toFixed(1)}%</div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">📅</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">{comparisonYear2} Ratio</div>
                      <div className="gw-stat-value">{comparisonData.summary.data2.extractionRatio.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              ) : currentData && (
                <div className="gw-stats-grid">
                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">🌧️</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Rainfall</div>
                      <div className="gw-stat-value">{currentData.rainfall} mm</div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">💧</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Extractable GW Resources</div>
                      <div className="gw-stat-value">{currentData.extractable} ham</div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">⚡</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">GW Extraction</div>
                      <div className="gw-stat-value">{currentData.extraction} ham</div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">🔄</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">GW Recharge</div>
                      <div className="gw-stat-value">{currentData.recharge} ham</div>
                    </div>
                  </div>

                  <div className="gw-stat-card">
                    <div className="gw-stat-icon">📉</div>
                    <div className="gw-stat-content">
                      <div className="gw-stat-label">Natural Discharge</div>
                      <div className="gw-stat-value">{currentData.discharge} ham</div>
                    </div>
                  </div>

                  {currentData.category && (
                    <div className="gw-stat-card gw-category-card">
                      <div className="gw-stat-icon">🎯</div>
                      <div className="gw-stat-content">
                        <div className="gw-stat-label">Category</div>
                        <div
                          className="gw-category-badge"
                          style={{
                            backgroundColor: categoryColors[currentData.category],
                            color: '#fff'
                          }}
                        >
                          {currentData.category}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
              
        {/* Village Detail Section */}
        {selectedVillage && (
          <div ref={villageDetailRef} className="gw-village-detail-section">
            <VillageDetailModal
              village={selectedVillage}
              onClose={() => setSelectedVillage(null)}
              userState={selectedState}
              userDistrict={selectedDistrict}
            />
          </div>
        )}


        {/* Table Section - Below Map */}
        <div className="gw-table-section">
          <div className="gw-table-container">
            <div className="gw-table-header">
              <h3>
                {comparisonMode 
                  ? `Comparison: ${comparisonYear1} vs ${comparisonYear2}` 
                  : (selectedState === null ? 'State-wise Data' : `${selectedState} - District-wise Data`)
                }
              </h3>
              <div className="gw-table-count">
                {comparisonMode 
                  ? (selectedState ? comparisonData?.districts?.length || 0 : comparisonData?.states?.length || 0)
                  : tableData.length
                } {selectedState === null ? 'States' : 'Districts'}
              </div>
            </div>

            <div className="gw-table-wrapper">
              {comparisonMode && comparisonData ? (
                // Comparison Table
                <table className="gw-table gw-comparison-table">
                  <thead>
                    <tr>
                      <th rowSpan="2">Name</th>
                      <th colSpan="2">{comparisonYear1}</th>
                      <th colSpan="2">{comparisonYear2}</th>
                      <th colSpan="2">Change</th>
                      <th rowSpan="2">Status</th>
                    </tr>
                    <tr>
                      <th>Extraction Ratio</th>
                      <th>Category</th>
                      <th>Extraction Ratio</th>
                      <th>Category</th>
                      <th>Δ Ratio</th>
                      <th>Δ Extraction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedState ? comparisonData.districts : comparisonData.states).map((row, index) => (
                      <tr
                        key={`${row.name}-${index}`}
                        className="gw-table-row"
                        onClick={() => {
                          if (!selectedState) {
                            setSelectedState(row.name);
                            setSelectedLocation(row.name);
                            zoomToState(row.name);
                          }
                        }}
                      >
                        <td className="gw-name-cell">{row.name}</td>
                        <td>{row.data1.extractionRatio.toFixed(1)}%</td>
                        <td>
                          <span
                            className="gw-category-badge-small"
                            style={{
                              backgroundColor: categoryColors[row.data1.category] || categoryColors['No Data'],
                              color: '#fff'
                            }}
                          >
                            {row.data1.category}
                          </span>
                        </td>
                        <td>{row.data2.extractionRatio.toFixed(1)}%</td>
                        <td>
                          <span
                            className="gw-category-badge-small"
                            style={{
                              backgroundColor: categoryColors[row.data2.category] || categoryColors['No Data'],
                              color: '#fff'
                            }}
                          >
                            {row.data2.category}
                          </span>
                        </td>
                        <td className={row.changes.extractionRatio > 0 ? 'gw-negative-change' : 'gw-positive-change'}>
                          {row.changes.extractionRatio > 0 ? '+' : ''}{row.changes.extractionRatio.toFixed(1)}%
                        </td>
                        <td className={row.changes.extraction > 0 ? 'gw-negative-change' : 'gw-positive-change'}>
                          {row.changes.extraction > 0 ? '+' : ''}{row.changes.extraction.toFixed(2)}
                        </td>
                        <td>
                          <span
                            className="gw-category-badge-small"
                            style={{
                              backgroundColor: comparisonColors[row.classification] || comparisonColors['No Data'],
                              color: '#fff'
                            }}
                          >
                            {row.classification.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                // Normal Table
                <table className="gw-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Rainfall (mm)</th>
                      <th>Extractable GW (ham)</th>
                      <th>GW Extraction (ham)</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData && tableData.length > 0 ? (
                      tableData.map((row, index) => (
                        <tr
                          key={`${row.name}-${index}`}
                          className={`gw-table-row ${
                            (selectedState === null && row.name === selectedState) ||
                            (selectedState !== null && row.name === selectedDistrict)
                              ? 'selected'
                              : ''
                          }`}
                          onClick={() => {
                            if (selectedState === null) {
                              // Clicking on a state row
                              setSelectedState(row.name);
                              setSelectedLocation(row.name);
                              setSelectedDistrict(null);
                              // Zoom to the state on the map
                              zoomToState(row.name);
                            } else {
                              // Clicking on a district row
                              setSelectedDistrict(row.name);
                              setSelectedLocation(`${row.name}, ${selectedState}`);
                              // Keep the current zoom level for districts
                            }
                          }}
                        >
                          <td className="gw-name-cell">{row.name || 'N/A'}</td>
                          <td>{row.rainfall ? row.rainfall.toFixed(2) : 'N/A'}</td>
                          <td>{(row.annualExtractableGroundWaterResources || row.extractable || 0).toFixed(2)}</td>
                          <td>{(row.groundWaterExtraction || row.extraction || 0).toFixed(2)}</td>
                          <td>
                            <span
                              className="gw-category-badge-small"
                              style={{
                                backgroundColor: categoryColors[row.category] || categoryColors['No Data'],
                                color: '#fff'
                              }}
                            >
                              {row.category || 'No Data'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                          {loading ? 'Loading data...' : 'No data available'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Data Source Attribution */}
        <div className="gw-data-source">
          <div className="gw-data-source-content">
            <div className="gw-data-source-icon">ℹ️</div>
            <div className="gw-data-source-text">
              <strong>Data Source:</strong> Central Ground Water Board, Department of Water Resources, River Development & Ganga Rejuvenation, Ministry of Jal Shakti, Government of India
            </div>
          </div>
        </div>

        {/* Insights & Summary Section */}
        {comparisonMode && comparisonData ? (
          // Comparison Insights
          <div className="gw-summary-section">
            <div className="gw-summary-container">
              <div className="gw-summary-header">
                <h3>
                  <span className="gw-summary-icon">📊</span>
                  Comparison Analysis
                </h3>
                <div className="gw-summary-location">
                  {comparisonYear1} vs {comparisonYear2}
                </div>
              </div>

              <div className="gw-summary-content">
                {/* Status Card */}
                <div className="gw-summary-status-card" style={{ 
                  borderLeftColor: comparisonColors[comparisonData.summary.classification] 
                }}>
                  <div className="gw-status-header">
                    <span className="gw-status-icon">
                      {comparisonData.summary.classification.includes('improved') ? '✅' : 
                       comparisonData.summary.classification.includes('degraded') ? '⚠️' : '➖'}
                    </span>
                    <h4 style={{ color: comparisonColors[comparisonData.summary.classification] }}>
                      {comparisonData.summary.classification.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h4>
                  </div>
                  <div className="gw-status-metric">
                    <span className="gw-metric-label">Extraction Ratio Change:</span>
                    <span className="gw-metric-value" style={{ 
                      color: comparisonData.summary.changes.extractionRatio > 0 ? '#d32f2f' : '#2e7d32'
                    }}>
                      {comparisonData.summary.changes.extractionRatio > 0 ? '+' : ''}
                      {comparisonData.summary.changes.extractionRatio.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {/* Explanation */}
                <div className="gw-summary-explanation">
                  <h4>What Changed</h4>
                  <p>
                    Between {comparisonYear1} and {comparisonYear2}, the groundwater extraction ratio 
                    {comparisonData.summary.changes.extractionRatio > 0 ? ' increased' : ' decreased'} by{' '}
                    {Math.abs(comparisonData.summary.changes.extractionRatio).toFixed(1)}%. 
                    {comparisonData.summary.changes.extractionRatio > 10 
                      ? ' This is a significant deterioration in groundwater sustainability.'
                      : comparisonData.summary.changes.extractionRatio < -10
                      ? ' This represents a significant improvement in groundwater management.'
                      : ' This indicates relatively stable groundwater conditions.'}
                  </p>
                  <div className="gw-summary-stats">
                    <div className="gw-stat-item">
                      <span className="gw-stat-label">Rainfall Change</span>
                      <span className="gw-stat-value">
                        {comparisonData.summary.changes.rainfall > 0 ? '+' : ''}
                        {comparisonData.summary.changes.rainfall.toFixed(2)} mm
                      </span>
                    </div>
                    <div className="gw-stat-item">
                      <span className="gw-stat-label">Extraction Change</span>
                      <span className="gw-stat-value">
                        {comparisonData.summary.changes.extraction > 0 ? '+' : ''}
                        {comparisonData.summary.changes.extraction.toFixed(2)} ham
                      </span>
                    </div>
                    <div className="gw-stat-item">
                      <span className="gw-stat-label">Extractable Change</span>
                      <span className="gw-stat-value">
                        {comparisonData.summary.changes.extractable > 0 ? '+' : ''}
                        {comparisonData.summary.changes.extractable.toFixed(2)} ham
                      </span>
                    </div>
                  </div>
                </div>

                {/* District-Level Analysis */}
                {!selectedState && comparisonData.allDistricts && comparisonData.allDistricts.length > 0 && (
                  <div className="gw-summary-recommendations">
                    <h4>📍 District-Level Analysis ({comparisonData.allDistricts.length} Districts)</h4>
                    <div className="gw-severity-summary">
                      <div className="gw-severity-item" style={{ backgroundColor: '#e8f5e9' }}>
                        <span className="gw-severity-label">🟢 Significantly Improved:</span>
                        <span className="gw-severity-count" style={{ color: '#0d7a0d', fontWeight: 'bold' }}>
                          {comparisonData.allDistricts.filter(r => r.classification === 'significantly_improved').length}
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>
                            ({((comparisonData.allDistricts.filter(r => r.classification === 'significantly_improved').length / comparisonData.allDistricts.length) * 100).toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#e8f5e9' }}>
                        <span className="gw-severity-label">🟢 Improved:</span>
                        <span className="gw-severity-count" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                          {comparisonData.allDistricts.filter(r => r.classification === 'improved').length}
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>
                            ({((comparisonData.allDistricts.filter(r => r.classification === 'improved').length / comparisonData.allDistricts.length) * 100).toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#f5f5f5' }}>
                        <span className="gw-severity-label">⚪ Neutral:</span>
                        <span className="gw-severity-count" style={{ color: '#666', fontWeight: 'bold' }}>
                          {comparisonData.allDistricts.filter(r => r.classification === 'neutral').length}
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>
                            ({((comparisonData.allDistricts.filter(r => r.classification === 'neutral').length / comparisonData.allDistricts.length) * 100).toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#fff3e0' }}>
                        <span className="gw-severity-label">🟠 Degraded:</span>
                        <span className="gw-severity-count" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                          {comparisonData.allDistricts.filter(r => r.classification === 'degraded').length}
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>
                            ({((comparisonData.allDistricts.filter(r => r.classification === 'degraded').length / comparisonData.allDistricts.length) * 100).toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#ffebee' }}>
                        <span className="gw-severity-label">🔴 Significantly Degraded:</span>
                        <span className="gw-severity-count" style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                          {comparisonData.allDistricts.filter(r => r.classification === 'significantly_degraded').length}
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>
                            ({((comparisonData.allDistricts.filter(r => r.classification === 'significantly_degraded').length / comparisonData.allDistricts.length) * 100).toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                    </div>
                    
                    {/* Overall Trend */}
                    <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                      <strong>Overall Trend:</strong> {' '}
                      {(() => {
                        const improved = comparisonData.allDistricts.filter(r => r.classification === 'significantly_improved' || r.classification === 'improved').length;
                        const degraded = comparisonData.allDistricts.filter(r => r.classification === 'significantly_degraded' || r.classification === 'degraded').length;
                        const total = comparisonData.allDistricts.length;
                        
                        if (improved > degraded) {
                          return `✅ Positive - ${improved} districts (${((improved/total)*100).toFixed(1)}%) improving vs ${degraded} (${((degraded/total)*100).toFixed(1)}%) degrading`;
                        } else if (degraded > improved) {
                          return `⚠️ Concerning - ${degraded} districts (${((degraded/total)*100).toFixed(1)}%) degrading vs ${improved} (${((improved/total)*100).toFixed(1)}%) improving`;
                        } else {
                          return `➖ Balanced - Equal number of improving and degrading districts`;
                        }
                      })()}
                    </div>
                  </div>
                )}

                {/* State-Level Analysis (when state is selected) */}
                {selectedState && comparisonData.districts && comparisonData.districts.length > 0 && (
                  <div className="gw-summary-recommendations">
                    <h4>📍 {selectedState} District Analysis ({comparisonData.districts.length} Districts)</h4>
                    <div className="gw-severity-summary">
                      <div className="gw-severity-item" style={{ backgroundColor: '#e8f5e9' }}>
                        <span className="gw-severity-label">🟢 Significantly Improved:</span>
                        <span className="gw-severity-count" style={{ color: '#0d7a0d', fontWeight: 'bold' }}>
                          {comparisonData.districts.filter(r => r.classification === 'significantly_improved').length}
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#e8f5e9' }}>
                        <span className="gw-severity-label">🟢 Improved:</span>
                        <span className="gw-severity-count" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                          {comparisonData.districts.filter(r => r.classification === 'improved').length}
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#f5f5f5' }}>
                        <span className="gw-severity-label">⚪ Neutral:</span>
                        <span className="gw-severity-count" style={{ color: '#666', fontWeight: 'bold' }}>
                          {comparisonData.districts.filter(r => r.classification === 'neutral').length}
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#fff3e0' }}>
                        <span className="gw-severity-label">🟠 Degraded:</span>
                        <span className="gw-severity-count" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                          {comparisonData.districts.filter(r => r.classification === 'degraded').length}
                        </span>
                      </div>
                      <div className="gw-severity-item" style={{ backgroundColor: '#ffebee' }}>
                        <span className="gw-severity-label">🔴 Significantly Degraded:</span>
                        <span className="gw-severity-count" style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                          {comparisonData.districts.filter(r => r.classification === 'significantly_degraded').length}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Top Performers and Concerns */}
                {!selectedState && comparisonData.allDistricts && comparisonData.allDistricts.length > 0 && (
                  <div className="gw-summary-recommendations">
                    <h4>🏆 Top Performers & 🚨 Areas of Concern</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      {/* Top Improving */}
                      <div style={{ padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '5px' }}>
                        <strong style={{ color: '#0d7a0d' }}>✨ Most Improved Districts:</strong>
                        <ul style={{ margin: '10px 0 0 0', padding: '0 0 0 20px', fontSize: '14px' }}>
                          {comparisonData.allDistricts
                            .filter(d => d.classification === 'significantly_improved')
                            .sort((a, b) => a.changes.extractionRatio - b.changes.extractionRatio)
                            .slice(0, 5)
                            .map((d, i) => (
                              <li key={i} style={{ marginBottom: '5px' }}>
                                <strong>{d.name}</strong>, {d.stateName}
                                <br/>
                                <span style={{ fontSize: '12px', color: '#2e7d32' }}>
                                  {d.changes.extractionRatio.toFixed(1)}% improvement
                                </span>
                              </li>
                            ))}
                        </ul>
                      </div>
                      
                      {/* Most Degraded */}
                      <div style={{ padding: '10px', backgroundColor: '#ffebee', borderRadius: '5px' }}>
                        <strong style={{ color: '#d32f2f' }}>⚠️ Most Degraded Districts:</strong>
                        <ul style={{ margin: '10px 0 0 0', padding: '0 0 0 20px', fontSize: '14px' }}>
                          {comparisonData.allDistricts
                            .filter(d => d.classification === 'significantly_degraded')
                            .sort((a, b) => b.changes.extractionRatio - a.changes.extractionRatio)
                            .slice(0, 5)
                            .map((d, i) => (
                              <li key={i} style={{ marginBottom: '5px' }}>
                                <strong>{d.name}</strong>, {d.stateName}
                                <br/>
                                <span style={{ fontSize: '12px', color: '#c62828' }}>
                                  +{d.changes.extractionRatio.toFixed(1)}% increase
                                </span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Category Transitions */}
                {!selectedState && comparisonData.allDistricts && comparisonData.allDistricts.length > 0 && (
                  <div className="gw-summary-recommendations">
                    <h4>🔄 Category Transitions</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                      {(() => {
                        const transitions = {
                          'Safe → Safe': 0,
                          'Safe → Semi Critical': 0,
                          'Safe → Critical': 0,
                          'Safe → Over Exploited': 0,
                          'Semi Critical → Safe': 0,
                          'Semi Critical → Critical': 0,
                          'Semi Critical → Over Exploited': 0,
                          'Critical → Safe': 0,
                          'Critical → Semi Critical': 0,
                          'Critical → Over Exploited': 0,
                          'Over Exploited → Critical': 0,
                          'Over Exploited → Safe': 0,
                          'Over Exploited → Over Exploited': 0
                        };
                        
                        comparisonData.allDistricts.forEach(d => {
                          const key = `${d.data1.category} → ${d.data2.category}`;
                          if (transitions.hasOwnProperty(key)) {
                            transitions[key]++;
                          }
                        });
                        
                        // Show only significant transitions
                        return Object.entries(transitions)
                          .filter(([_, count]) => count > 0)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 6)
                          .map(([transition, count]) => {
                            const [from, to] = transition.split(' → ');
                            const isImproving = 
                              (from === 'Over Exploited' && to !== 'Over Exploited') ||
                              (from === 'Critical' && (to === 'Semi Critical' || to === 'Safe')) ||
                              (from === 'Semi Critical' && to === 'Safe');
                            const isDegrading = 
                              (to === 'Over Exploited' && from !== 'Over Exploited') ||
                              (to === 'Critical' && (from === 'Semi Critical' || from === 'Safe')) ||
                              (to === 'Semi Critical' && from === 'Safe');
                            
                            return (
                              <div key={transition} style={{ 
                                padding: '8px', 
                                backgroundColor: isImproving ? '#e8f5e9' : isDegrading ? '#ffebee' : '#f5f5f5',
                                borderRadius: '5px',
                                fontSize: '13px'
                              }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>
                                  {isImproving ? '✅' : isDegrading ? '⚠️' : '➖'} {transition}
                                </div>
                                <div style={{ color: '#666' }}>{count} districts</div>
                              </div>
                            );
                          });
                      })()}
                    </div>
                  </div>
                )}

                {/* Key Terms */}
                <div className="gw-summary-terms">
                  <h4>Understanding the Comparison</h4>
                  <div className="gw-terms-grid">
                    <div className="gw-term-item">
                      <strong>Significantly Improved:</strong>
                      <span>Extraction ratio decreased by more than 10%, indicating much better groundwater management.</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Improved:</strong>
                      <span>Extraction ratio decreased by 5-10%, showing positive progress in sustainability.</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Neutral:</strong>
                      <span>Extraction ratio changed by less than 5%, indicating stable conditions.</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Degraded:</strong>
                      <span>Extraction ratio increased by 5-10%, showing concerning trends.</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Significantly Degraded:</strong>
                      <span>Extraction ratio increased by more than 10%, indicating severe deterioration.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : summary && (
          <div className="gw-summary-section">
            <div className="gw-summary-container">
              <div className="gw-summary-header">
                <h3>
                  <span className="gw-summary-icon">💡</span>
                  Insights & Analysis
                </h3>
                <div className="gw-summary-location">{summary.location}</div>
              </div>

              <div className="gw-summary-content">
                {/* Status Card */}
                <div className="gw-summary-status-card" style={{ borderLeftColor: summary.statusColor }}>
                  <div className="gw-status-header">
                    <span className="gw-status-icon">{summary.statusIcon}</span>
                    <h4 style={{ color: summary.statusColor }}>{summary.status}</h4>
                  </div>
                  <div className="gw-status-metric">
                    <span className="gw-metric-label">Groundwater Extraction Rate:</span>
                    <span className="gw-metric-value" style={{ color: summary.statusColor }}>
                      {summary.extractionPercentage}%
                    </span>
                  </div>
                </div>

                {/* Explanation */}
                <div className="gw-summary-explanation">
                  <h4>What This Means</h4>
                  <p>
                    {summary.explanation} The region {summary.rainfallContext}.
                  </p>
                  <div className="gw-summary-stats">
                    <div className="gw-stat-item">
                      <span className="gw-stat-label">Annual Rainfall</span>
                      <span className="gw-stat-value">{currentData.rainfall} mm</span>
                    </div>
                    <div className="gw-stat-item">
                      <span className="gw-stat-label">Extractable Resources</span>
                      <span className="gw-stat-value">{currentData.extractable} ham</span>
                    </div>
                    <div className="gw-stat-item">
                      <span className="gw-stat-label">Current Extraction</span>
                      <span className="gw-stat-value">{currentData.extraction} ham</span>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="gw-summary-recommendations">
                  <h4>Recommended Actions</h4>
                  <ul className="gw-recommendations-list">
                    {summary.recommendations.map((rec, index) => (
                      <li key={index}>
                        <span className="gw-rec-bullet">→</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Key Terms */}
                <div className="gw-summary-terms">
                  <h4>Understanding the Terms</h4>
                  <div className="gw-terms-grid">
                    <div className="gw-term-item">
                      <strong>Extractable Groundwater (ham):</strong>
                      <span>The amount of groundwater that can be safely extracted annually without depleting the aquifer. Measured in hectare-meters (ham).</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Groundwater Extraction:</strong>
                      <span>The actual amount of groundwater being pumped out for agriculture, industry, and domestic use.</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Extraction Rate:</strong>
                      <span>Percentage of extractable resources being used. Above 100% means unsustainable extraction.</span>
                    </div>
                    <div className="gw-term-item">
                      <strong>Recharge:</strong>
                      <span>Natural replenishment of groundwater through rainfall, rivers, and other sources.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* District Trends Panel - Show when district is selected
        {selectedDistrict && (
          <DistrictTrendsPanel
            state={selectedState}
            district={selectedDistrict}
            categoryColors={categoryColors}
          />
        )} */}

        {/* Village-Level Data Panel - Show when district is selected and villages are loaded */}
        {selectedDistrict && filteredVillageData && filteredVillageData.length > 0 && (
          <VillageDataPanel
            villages={filteredVillageData}
            onVillageSelect={handleVillageSelect}
            onVillageHover={(village) => {
              setHoveredVillage(village);
            }}
            categoryColors={categoryColors}
            userState={userState}
            userDistrict={userDistrict}
          />
        )}
          </>
        ) : (
          /* Trend Analysis Tab */
          <TrendAnalysis 
            selectedState={selectedState}
            selectedDistrict={selectedDistrict}
          />
        )}
      </div>

      {/* Village Detail Modal */}
      {showVillageModal && selectedVillage && (
        <VillageDetailModal
          village={selectedVillage}
          onClose={() => {
            setShowVillageModal(false);
          }}
        />
      )}
    </div>
  );
};

export default GroundwaterDashboard;